import { useCallback, useEffect, useRef, useState } from 'react';
import { BngApiClient } from '../module/apiClients/businessNames.apiClient';
import { useIsMobile } from './useIsMobile';
export const NAMES_PER_BATCH = 9; // 3 columns × 3 rows per the 3×3 grid UX decision (2026-05-05)
export const NAMES_PER_BATCH_MOBILE = 6; // 2 columns × 3 rows on mobile
const REGENERATE_THRESHOLD = 3; // batches before CTA flips to "Regenerate"
const EXCLUDING_WIRE_CAP = 360;
const getHttpErrorStatus = error => {
  var _httpError$response;
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const httpError = error;
  return ((_httpError$response = httpError.response) == null ? void 0 : _httpError$response.status) ?? httpError.status;
};
const classifyError = err => {
  if (err instanceof Error && err.message === 'Network Error') {
    return 'NetworkIssue';
  }
  const status = getHttpErrorStatus(err);
  if (status === 500 || status === 429) {
    return 'ServerError';
  }
  return 'TechnicalIssue';
};
const withStableIds = names => names.map(name => name.id ? name : {
  ...name,
  id: name.name
});
export const useBusinessNames = _ref => {
  let {
    query,
    enabled = true,
    namesPerBatch: namesPerBatchOverride
  } = _ref;
  const isMobile = useIsMobile();
  const namesPerBatch = namesPerBatchOverride ?? (isMobile ? NAMES_PER_BATCH_MOBILE : NAMES_PER_BATCH);
  const {
    generateBusinessNames
  } = BngApiClient.useQueries();
  const [visibleNames, setVisibleNames] = useState([]);
  // Names ever shown this session — sent as `excluding` so the AI never
  // re-suggests a name the user has already seen.
  const excludingAllRef = useRef(new Set());
  const [batchCount, setBatchCount] = useState(0);
  const [selectedKey, setSelectedKey] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(undefined);
  // Monotonic counter — incremented before each new logical fetch session.
  // Responses from superseded fetches check this and discard themselves.
  const fetchGenRef = useRef(0);
  // True only on the very first render; prevents the query-change effect
  // from firing a second fetch on mount.
  const isMountRef = useRef(true);

  // Pure network + normalisation function. All state transitions happen in
  // the callers (useEffect / callbacks) — not inside this function.
  const fetchBatch = useCallback(async mode => {
    const myGen = ++fetchGenRef.current;
    setIsLoading(true);
    setError(undefined);
    try {
      const excluding = mode === 'initial' ? [] : [...excludingAllRef.current].slice(-EXCLUDING_WIRE_CAP);
      const {
        names: rawNames
      } = await generateBusinessNames({
        query,
        excluding,
        count: namesPerBatch
      });
      if (fetchGenRef.current !== myGen) {
        return;
      } // stale — a newer fetch is in flight
      if (rawNames.length === 0) {
        setError('TechnicalIssue');
        return;
      }
      const names = withStableIds(rawNames);
      names.forEach(n => {
        if (n.name) {
          excludingAllRef.current.add(n.name.toLowerCase());
        }
      });
      if (mode === 'append') {
        setVisibleNames(prev => [...prev, ...names]);
        setBatchCount(c => c + 1);
      } else {
        setVisibleNames(names);
        setBatchCount(1);
        setSelectedKey(names[0].id);
      }
    } catch (err) {
      if (fetchGenRef.current !== myGen) {
        return;
      }
      setError(classifyError(err));
    } finally {
      if (fetchGenRef.current === myGen) {
        setIsLoading(false);
      }
    }
  }, [generateBusinessNames, query, namesPerBatch]);

  // Fire the initial fetch on mount; reset all state and re-fetch on query change.
  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      if (query && enabled) {
        fetchBatch('initial');
      }
      return;
    }
    // Query changed — invalidate any in-flight response and start fresh.
    fetchGenRef.current++;
    excludingAllRef.current = new Set();
    setVisibleNames([]);
    setBatchCount(0);
    setSelectedKey(undefined);
    setError(undefined);
    setIsLoading(false);
    if (query && enabled) {
      fetchBatch('initial');
    }
    // fetchBatch is intentionally excluded: its identity changes when query
    // changes, which would cause an extra re-run before React can batch them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, enabled]);
  const generateMore = useCallback(() => {
    if (batchCount >= REGENERATE_THRESHOLD || isLoading) {
      return;
    }
    fetchBatch('append');
  }, [batchCount, isLoading, fetchBatch]);
  const regenerate = useCallback(() => {
    if (isLoading) {
      return;
    }
    // Do NOT reset excludingAllRef — the API uses it to avoid re-suggesting
    // names the user has already seen. Clearing it caused regenerate to return
    // the same original batch every time.
    //
    // Do NOT reset batchCount/selectedKey/visibleNames upfront — if the fetch
    // fails (e.g. offline) the current names and ctaState remain intact so the
    // user can retry. fetchBatch overwrites them only on a successful response.
    setError(undefined);
    fetchBatch('replace');
  }, [isLoading, fetchBatch]);
  const retry = useCallback(() => {
    fetchGenRef.current++;
    excludingAllRef.current = new Set();
    setVisibleNames([]);
    setBatchCount(0);
    setSelectedKey(undefined);
    setError(undefined);
    if (query && enabled) {
      fetchBatch('initial');
    }
  }, [query, enabled, fetchBatch]);
  const selectName = useCallback(name => {
    setSelectedKey(name.id);
  }, []);
  const selectedName = visibleNames.find(_ref2 => {
    let {
      id
    } = _ref2;
    return id === selectedKey;
  });
  return {
    names: visibleNames,
    selectedName,
    selectName,
    isLoading: batchCount === 0 && isLoading,
    isFetchingMore: batchCount > 0 && isLoading,
    isReady: batchCount > 0 && error === undefined,
    hasError: error !== undefined,
    error,
    ctaState: batchCount >= REGENERATE_THRESHOLD ? 'regenerate' : 'generateMore',
    generateMore,
    regenerate,
    retry,
    namesPerBatch
  };
};
//# sourceMappingURL=useBusinessNames.js.map